import type { LlmDebugContext } from '../../common/types/debug-context';
import type { LlmStreamEvent } from '../../common/types/llm-stream';
import { startBaseAssistantStream, stopBaseAssistantStream } from '../rpc';
import { llmStreamChannelClient } from '../messaging';
import logger from '@ayocore/platform/common/helpers/logger';

export type ToolCallStatus = 'pending' | 'executing' | 'completed' | 'error' | 'waiting_user_input';

export interface ToolCallInfo {
  name: string;
  call_id: string;
  status: ToolCallStatus;
}

export interface StreamMetadata {
  model?: string;
  processing_time_ms?: number;
  iteration?: number;
  [key: string]: unknown;
}

export interface StreamEventLog {
  event: Record<string, unknown>;
  timestamp: Date;
}

export interface LlmStreamState {
  // Core
  content: string;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
  iteration: number | null;
  metadata: StreamMetadata | null;
  totalChunks: number;
  debugContext: LlmDebugContext | null;

  // Tools
  toolCalls: Map<string, ToolCallInfo>;
  toolCallStatuses: Array<{ id: string; status: ToolCallStatus }>;
  pendingUserInput: { call_id: string; prompt: string } | null;

  // Debug
  eventLog: StreamEventLog[];
  eventLogEnabled: boolean;
  eventLogSize: number;
}

type Listener = () => void;

/**
 * Контроллер состояния LLM-стрима.
 * Хранит состояние вне React и предоставляет API для безопасных обновлений и подписки.
 */
export class LlmStreamController {
  private static instance: LlmStreamController | null = null;
  private listeners: Set<Listener> = new Set();
  /** Реестр активных подписок на стримы: sessionId -> unsubscribe функция */
  private activeSubscriptions: Map<string, () => void> = new Map();
  private state: LlmStreamState = {
    content: '',
    isLoading: false,
    error: null,
    sessionId: null,
    iteration: null,
    metadata: null,
    totalChunks: 0,
    debugContext: null,
    toolCalls: new Map<string, ToolCallInfo>(),
    toolCallStatuses: [],
    pendingUserInput: null,
    eventLog: [],
    eventLogEnabled: false,
    eventLogSize: 0,
  };

  static getInstance(): LlmStreamController {
    if (!this.instance) this.instance = new LlmStreamController();
    return this.instance;
  }

  // Subscribe / Snapshot
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): LlmStreamState => this.state;

  // Internal emit
  private emit(): void {
    for (const l of this.listeners) l();
  }

  // Mutations
  setLoading(flag: boolean): void {
    this.state = { ...this.state, isLoading: flag };
    this.emit();
  }

  setError(message: string | null): void {
    this.state = { ...this.state, error: message };
    this.emit();
  }

  setSession(sessionId: string | null): void {
    this.state = { ...this.state, sessionId };
    this.emit();
  }

  setDebugContext(ctx: LlmDebugContext | null): void {
    this.state = { ...this.state, debugContext: ctx };
    this.emit();
  }

  setContentDelta(delta: string): void {
    if (!delta) return;
    this.state = { ...this.state, content: this.state.content + delta };
    this.emit();
  }

  setIteration(iteration: number | null): void {
    this.state = { ...this.state, iteration };
    this.emit();
  }

  setTotalChunks(n: number): void {
    // chunkIndex обычно 0-based → переводим в счётчик
    const count = typeof n === 'number' && n >= 0 ? n + 1 : n;
    this.state = { ...this.state, totalChunks: Math.max(this.state.totalChunks, count) };
    this.emit();
  }

  mergeMetadata(meta?: StreamMetadata | null): void {
    if (!meta) return;
    const metadata = { ...(this.state.metadata ?? {}), ...meta };
    this.state = { ...this.state, metadata };
    this.emit();
  }

  setEventLogEnabled(enabled: boolean): void {
    this.state = { ...this.state, eventLogEnabled: enabled };
    if (!enabled) {
      // не чистим eventLog, просто перестаём пушить
      this.emit();
      return;
    }
    this.emit();
  }

  appendEventLog(event: Record<string, unknown>): void {
    if (!this.state.eventLogEnabled) return;
    const next = [...this.state.eventLog, { event, timestamp: new Date() }].slice(-100);
    this.state = { ...this.state, eventLog: next, eventLogSize: next.length };
    this.emit();
  }

  clearEventLog(): void {
    this.state = { ...this.state, eventLog: [], eventLogSize: 0 };
    this.emit();
  }

  // Tools / Pending
  upsertToolCall(callId: string, status: ToolCallStatus, name = 'unknown'): void {
    const next = new Map(this.state.toolCalls);
    next.set(callId, { name, call_id: callId, status });
    const toolCallStatuses = Array.from(next.values()).map(tc => ({
      id: tc.call_id,
      status: tc.status,
    }));
    this.state = { ...this.state, toolCalls: next, toolCallStatuses };
    this.emit();
  }

  resetToolCalls(): void {
    this.state = { ...this.state, toolCalls: new Map(), toolCallStatuses: [] };
    this.emit();
  }

  /**
   * Управление подписками на стрим-события
   */

  /**
   * Зарегистрировать активную подписку для сессии
   * Если для sessionId уже есть подписка, отписываемся от старой перед регистрацией новой
   *
   * @param sessionId - UUID стрим-сессии
   * @param unsubscribe - Функция отписки от подписки
   */
  registerSubscription(sessionId: string, unsubscribe: () => void): void {
    if (!sessionId) return;

    // ✅ Если для этого sessionId уже есть активная подписка, проверяем, не та же ли это подписка
    const existingUnsubscribe = this.activeSubscriptions.get(sessionId);
    if (existingUnsubscribe) {
      // Если это та же самая функция отписки, не делаем ничего (избегаем двойной отписки)
      if (existingUnsubscribe === unsubscribe) {
        logger.debug(
          `✅ [LlmStreamController] Subscription already registered for sessionId: ${sessionId}`
        );
        return;
      }
      // Если это другая подписка, отписываемся от старой
      logger.info(
        `🔄 [LlmStreamController] Unsubscribing from existing subscription for sessionId: ${sessionId}`
      );
      existingUnsubscribe();
    }

    // Регистрируем новую подписку
    this.activeSubscriptions.set(sessionId, unsubscribe);
    logger.debug(`📝 [LlmStreamController] Registered subscription for sessionId: ${sessionId}`);
  }

  /**
   * Отписаться от подписки для конкретной сессии
   *
   * @param sessionId - UUID стрим-сессии
   */
  unsubscribeSession(sessionId: string): void {
    if (!sessionId) return;

    const unsubscribe = this.activeSubscriptions.get(sessionId);
    if (unsubscribe) {
      unsubscribe();
      this.activeSubscriptions.delete(sessionId);
      logger.debug(`🗑️ [LlmStreamController] Unsubscribed from sessionId: ${sessionId}`);
    }
  }

  /**
   * Проверить, есть ли активная подписка для сессии
   *
   * @param sessionId - UUID стрим-сессии
   * @returns true, если есть активная подписка
   */
  hasActiveSubscription(sessionId: string): boolean {
    return this.activeSubscriptions.has(sessionId);
  }

  /**
   * Убедиться, что для сессии есть активная подписка на WebSocket.
   * Если подписки нет — создаёт её.
   *
   * @param sessionId - UUID стрим-сессии
   */
  ensureSubscription(sessionId: string): void {
    if (!sessionId) return;

    // Если подписка уже есть — ничего не делаем
    if (this.hasActiveSubscription(sessionId)) {
      logger.debug(
        `✅ [LlmStreamController] Subscription already active for sessionId: ${sessionId}`
      );
      return;
    }

    // Создаём подписку на события стрима
    const unsubscribe = llmStreamChannelClient.onMessage(event => {
      // Фильтруем события только для нашей сессии
      if (!event.sessionId || event.sessionId !== sessionId) {
        return;
      }
      // Применяем событие к состоянию
      this.applyStreamEvent(event);
    });

    // Регистрируем подписку
    this.registerSubscription(sessionId, unsubscribe);
    logger.info(
      `✅ [LlmStreamController] WebSocket subscription created for sessionId: ${sessionId}`
    );
  }

  /**
   * Очистить все активные подписки (для полного сброса)
   */
  private clearAllSubscriptions(): void {
    for (const [sessionId, unsubscribe] of this.activeSubscriptions) {
      unsubscribe();
      logger.debug(`🗑️ [LlmStreamController] Unsubscribed from sessionId: ${sessionId}`);
    }
    this.activeSubscriptions.clear();
  }

  setPending(callId: string, prompt: string): void {
    this.state = { ...this.state, pendingUserInput: { call_id: callId, prompt } };
    this.emit();
  }

  clearPending(): void {
    if (!this.state.pendingUserInput) return;
    this.state = { ...this.state, pendingUserInput: null };
    this.emit();
  }

  // Lifecycle
  streamStart(): void {
    this.state = {
      ...this.state,
      content: '',
      isLoading: true,
      error: null,
      iteration: null,
      metadata: null,
      debugContext: null, // сбрасываем debug-контекст, чтобы не протекал между сессиями
      totalChunks: 0,
      // оставляем sessionId как есть (можем переиспользовать)
      toolCalls: new Map(),
      toolCallStatuses: [],
      pendingUserInput: null,
      // Не смешиваем события разных сессий — очищаем лог на старте
      eventLog: [],
      eventLogSize: 0,
    };
    this.emit();
  }

  streamDone(meta?: StreamMetadata | null): void {
    const next: Partial<LlmStreamState> = { isLoading: false };
    if (meta) next.metadata = { ...(this.state.metadata ?? {}), ...meta };
    this.state = {
      ...this.state,
      ...next,
      // Очистка pending/toolCalls после завершения
      pendingUserInput: null,
      toolCalls: new Map(),
      toolCallStatuses: [],
    };
    this.emit();
  }

  streamError(message: string, meta?: StreamMetadata | null): void {
    const next: Partial<LlmStreamState> = { isLoading: false, error: message };
    if (meta) next.metadata = { ...(this.state.metadata ?? {}), ...meta };
    this.state = {
      ...this.state,
      ...next,
      // Очистка pending/toolCalls при ошибке
      pendingUserInput: null,
      toolCalls: new Map(),
      toolCallStatuses: [],
    };
    this.emit();
  }

  resetAll(): void {
    // ✅ Очищаем все активные подписки при полном сбросе
    this.clearAllSubscriptions();

    this.state = {
      content: '',
      isLoading: false,
      error: null,
      sessionId: null,
      iteration: null,
      metadata: null,
      totalChunks: 0,
      debugContext: null,
      toolCalls: new Map(),
      toolCallStatuses: [],
      pendingUserInput: null,
      eventLog: [],
      eventLogSize: 0,
      eventLogEnabled: this.state.eventLogEnabled,
    };
    this.emit();
  }

  /**
   * Унифицированное применение событий стрима к состоянию контроллера.
   * Контроллер не занимается сетевым транспортом, только маппингом событий.
   */
  applyStreamEvent(event: LlmStreamEvent): void {
    // Лёгкий лог: пишем только при включённом дебаге
    this.appendEventLog(event as unknown as Record<string, unknown>);

    switch (event.streamType) {
      case 'stream_start': {
        this.streamStart();
        return;
      }
      case 'stream_chunk': {
        if (event.chunk) this.setContentDelta(event.chunk);
        if (event.chunkIndex !== undefined) this.setTotalChunks(event.chunkIndex);
        const meta = event.metadata;
        if (meta?.toolCall?.call_id) {
          const call = meta.toolCall;
          this.upsertToolCall(
            call.call_id!,
            (call.status ?? 'pending') as ToolCallStatus,
            call.name ?? 'unknown'
          );
        }
        if (event.metadata) this.mergeMetadata(event.metadata as StreamMetadata);
        return;
      }
      case 'iteration_start': {
        const meta = event.metadata;
        if (meta?.iteration !== undefined) this.setIteration(meta.iteration);
        return;
      }
      case 'iteration_done': {
        if (event.metadata) this.mergeMetadata(event.metadata as StreamMetadata);
        return;
      }
      case 'tool_requires_user_input': {
        const meta = event.metadata;
        const call = meta?.toolCall;
        if (call?.call_id && call.prompt) {
          this.setPending(call.call_id, call.prompt);
          this.upsertToolCall(call.call_id, 'waiting_user_input', call.name ?? 'unknown');
          // ✅ Разблокируем форму для ответа пользователя
          // Стрим приостановлен (pending), но форма должна быть активна
          this.setLoading(false);
        } else {
          logger.warn(
            `⚠️ [LlmStreamController] Invalid tool_requires_user_input event: ` +
              `call_id=${call?.call_id}, prompt=${call?.prompt}`
          );
        }
        return;
      }
      case 'stream_done': {
        this.streamDone(event.metadata as StreamMetadata | null);
        return;
      }
      case 'stream_cancelled': {
        // Отмена пользователем: финализируем как завершение (снимаем loading, чистим pending/
        // toolCalls). Частичный текст уже в content; сервер сохранил его с маркером в историю.
        this.streamDone(event.metadata as StreamMetadata | null);
        return;
      }
      case 'stream_error': {
        const msg = (event.metadata?.error as string) || 'Stream error';
        this.streamError(msg, event.metadata as StreamMetadata | null);
        return;
      }
      default:
        // no-op
        return;
    }
  }
}

// === Императивный API для запуска/ответа/сброса ===

export type StartRPCOptions = Parameters<typeof startBaseAssistantStream>[1];
export type StartRPCResult = Awaited<ReturnType<typeof startBaseAssistantStream>>;

export async function runLlmStream(
  userPrompt: string,
  options: StartRPCOptions = {}
): Promise<StartRPCResult> {
  const ctrl = LlmStreamController.getInstance();
  ctrl.streamStart();
  try {
    // Вызываем RPC напрямую
    const result = await startBaseAssistantStream(userPrompt, options);
    if (result.success) {
      if (result.sessionId) {
        ctrl.setSession(result.sessionId);
        // ✅ Убеждаемся, что подписка на WebSocket создана (для новых чатов без истории)
        ctrl.ensureSubscription(result.sessionId);
      }
      if (result.debugContext !== undefined) {
        ctrl.setDebugContext(result.debugContext ?? null);
      }
      return result;
    } else {
      ctrl.streamError(result.error || 'Stream failed');
      return result;
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Stream error';
    ctrl.streamError(msg);
    return { success: false, error: msg };
  }
}

/**
 * Ответить ожидающему инструменту (HITL-пауза) и продолжить ход.
 *
 * @param options - доп. опции хода (например прикреплённые файлы): ответ на паузу —
 *   такое же сообщение пользователя, к нему тоже можно приложить файл, поэтому опции
 *   обязаны доезжать и здесь (иначе вложение молча терялось бы).
 */
export async function answerPendingToolCall(
  callId: string,
  text: string,
  options?: StartRPCOptions
): Promise<StartRPCResult> {
  const ctrl = LlmStreamController.getInstance();
  // Переводим pending → executing и продолжаем стрим в той же сессии
  ctrl.clearPending();
  ctrl.upsertToolCall(callId, 'executing');
  // Сервер сам знает какой sessionId использовать
  return runLlmStream(text, { ...options, pendingCallId: callId });
}

export function resetLlmStream(): void {
  LlmStreamController.getInstance().resetAll();
}

/**
 * Прерывает текущий ход ассистента (кнопка «стоп»). Зовёт серверную stopBaseAssistantStream
 * по активному sessionId; фактическую финализацию (снятие loading) делает событие
 * `stream_cancelled`, которое сервер шлёт после отмены. Идемпотентно: без активного стрима
 * или sessionId — no-op.
 */
export async function stopLlmStream(): Promise<void> {
  const ctrl = LlmStreamController.getInstance();
  const { sessionId, isLoading } = ctrl.getSnapshot();
  if (!isLoading || !sessionId) return;
  try {
    await stopBaseAssistantStream(sessionId);
  } catch (err) {
    logger.warn(
      `⚠️ [LlmStreamController] stopLlmStream failed: ${err instanceof Error ? err.message : String(err)}`
    );
  }
}
